/**
 * Ülke adları — aktif dile göre (Intl.DisplayNames).
 */

export function countryFlagEmoji(code) {
  if (!code || code.length !== 2) return '';
  const cc = code.toUpperCase();
  // XK (Kosovo) bazı ortamlarda bayrak yok
  if (cc === 'XK') return '🇽🇰';
  try {
    return String.fromCodePoint(
      ...[...cc].map((c) => 127397 + c.charCodeAt(0)),
    );
  } catch {
    return '';
  }
}

/** i18n dil kodunu Intl locale'e çevir */
export function toIntlLocale(i18nLanguage) {
  if (!i18nLanguage) return 'en';
  if (i18nLanguage.startsWith('zh')) return 'zh-CN';
  return i18nLanguage;
}

/**
 * @param {string} code — ISO 3166-1 alpha-2
 * @param {string} language — i18n.language
 * @param {string} [fallback]
 */
export function getLocalizedCountryName(code, language, fallback = '') {
  if (!code) return fallback || '';
  const upper = String(code).toUpperCase();
  const locale = toIntlLocale(language);
  try {
    const dn = new Intl.DisplayNames([locale, locale.split('-')[0], 'en'], {
      type: 'region',
    });
    const name = dn.of(upper);
    if (name && name !== upper) return name;
  } catch {
    /* ignore */
  }
  // Kosovo vb. eksik region kodları
  if (upper === 'XK') {
    const xk = {
      tr: 'Kosova',
      de: 'Kosovo',
      en: 'Kosovo',
      fr: 'Kosovo',
      es: 'Kosovo',
      it: 'Kosovo',
      pt: 'Kosovo',
      ar: 'كوسوفو',
      ru: 'Косово',
      zh: '科索沃',
      fa: 'کوزوو',
    };
    const key = (locale || 'en').split('-')[0];
    return xk[key] || xk.en;
  }
  return fallback || upper;
}

/** ISO 3166-1 alpha-3 → alpha-2 (admin DEU, USA, GBR vb.) */
const ISO3_TO_ISO2 = {
  DEU: 'DE', TUR: 'TR', USA: 'US', GBR: 'GB', FRA: 'FR', ITA: 'IT', ESP: 'ES',
  NLD: 'NL', BEL: 'BE', AUT: 'AT', CHE: 'CH', SWE: 'SE', NOR: 'NO', DNK: 'DK',
  FIN: 'FI', POL: 'PL', GRC: 'GR', RUS: 'RU', CHN: 'CN', JPN: 'JP', CAN: 'CA',
  AUS: 'AU', ARE: 'AE', SAU: 'SA', QAT: 'QA', KWT: 'KW', EGY: 'EG', IRN: 'IR',
  IRQ: 'IQ', LBN: 'LB', JOR: 'JO', ISR: 'IL', MAR: 'MA', TUN: 'TN', DZA: 'DZ',
  PAK: 'PK', IND: 'IN', BGD: 'BD', AFG: 'AF', AZE: 'AZ', GEO: 'GE', KAZ: 'KZ',
  UKR: 'UA', ROU: 'RO', BGR: 'BG', HUN: 'HU', CZE: 'CZ', PRT: 'PT', IRL: 'IE',
  BRA: 'BR', MEX: 'MX', ARG: 'AR', KOR: 'KR', HKG: 'HK', TWN: 'TW', SGP: 'SG',
  MYS: 'MY', IDN: 'ID', THA: 'TH', VNM: 'VN', XKX: 'XK', PSE: 'PS', SYR: 'SY',
  CYP: 'CY', NZL: 'NZ', ZAF: 'ZA',
};

/** Tam ülke adı / ISO kod → ISO 3166-1 alpha-2. Bilinmiyorsa null (TR varsayılmaz). */
const COUNTRY_NAME_TO_CODE = {
  turkey: 'TR', türkiye: 'TR', turkiye: 'TR',
  germany: 'DE', almanya: 'DE',
  'united states': 'US', usa: 'US', america: 'US', amerika: 'US',
  'united kingdom': 'GB', uk: 'GB', england: 'GB', ingiltere: 'GB',
  france: 'FR', fransa: 'FR',
  netherlands: 'NL', holland: 'NL', hollanda: 'NL',
  belgium: 'BE', belçika: 'BE', belcika: 'BE',
  austria: 'AT', avusturya: 'AT',
  switzerland: 'CH', isviçre: 'CH', isvicre: 'CH',
  italy: 'IT', italya: 'IT',
  spain: 'ES', ispanya: 'ES',
  sweden: 'SE', isveç: 'SE', isvec: 'SE',
  norway: 'NO', norveç: 'NO', norvec: 'NO',
  denmark: 'DK', danimarka: 'DK',
  finland: 'FI', finlandiya: 'FI',
  poland: 'PL', polonya: 'PL',
  greece: 'GR', yunanistan: 'GR',
  russia: 'RU', rusya: 'RU',
  china: 'CN', çin: 'CN', cin: 'CN',
  japan: 'JP', japonya: 'JP',
  canada: 'CA', kanada: 'CA',
  australia: 'AU', avustralya: 'AU',
  'united arab emirates': 'AE', uae: 'AE', 'birleşik arap emirlikleri': 'AE',
  'saudi arabia': 'SA', 'suudi arabistan': 'SA',
  qatar: 'QA', katar: 'QA',
  kuwait: 'KW', kuveyt: 'KW',
  egypt: 'EG', mısır: 'EG', misir: 'EG',
  iran: 'IR',
  iraq: 'IQ', irak: 'IQ',
  lebanon: 'LB', lübnan: 'LB', lubnan: 'LB',
  jordan: 'JO', ürdün: 'JO', urdun: 'JO',
  israel: 'IL', israil: 'IL',
  morocco: 'MA', fas: 'MA',
  tunisia: 'TN', tunus: 'TN',
  algeria: 'DZ', cezayir: 'DZ',
  pakistan: 'PK',
  india: 'IN', hindistan: 'IN',
  bangladesh: 'BD',
  afghanistan: 'AF', afganistan: 'AF',
  azerbaijan: 'AZ', azerbaycan: 'AZ',
  georgia: 'GE', gürcistan: 'GE', gurcistan: 'GE',
  kazakhstan: 'KZ', kazakistan: 'KZ',
  ukraine: 'UA', ukrayna: 'UA',
  romania: 'RO', romanya: 'RO',
  bulgaria: 'BG', bulgaristan: 'BG',
  hungary: 'HU', macaristan: 'HU',
  czechia: 'CZ', 'czech republic': 'CZ', 'çek cumhuriyeti': 'CZ',
  portugal: 'PT', portekiz: 'PT',
  ireland: 'IE', irlanda: 'IE',
  brazil: 'BR', brezilya: 'BR',
  mexico: 'MX', meksika: 'MX',
  argentina: 'AR', arjantin: 'AR',
  'south korea': 'KR', 'güney kore': 'KR', korea: 'KR',
  'hong kong': 'HK',
  taiwan: 'TW',
  singapore: 'SG', singapur: 'SG',
  malaysia: 'MY', malezya: 'MY',
  indonesia: 'ID', endonezya: 'ID',
  thailand: 'TH', tayland: 'TH',
  vietnam: 'VN',
  kosovo: 'XK', kosova: 'XK',
  palestine: 'PS', filistin: 'PS',
  syria: 'SY', suriye: 'SY',
  cyprus: 'CY', kıbrıs: 'CY', kibris: 'CY',
};

export function normalizeCountryCode(country, { fallback = null } = {}) {
  if (!country || typeof country !== 'string') return fallback;
  const normalized = country.toLowerCase().trim();
  if (!normalized) return fallback;
  if (normalized.length === 2) return normalized.toUpperCase();
  if (normalized.length === 3) {
    const iso2 = ISO3_TO_ISO2[normalized.toUpperCase()];
    if (iso2) return iso2;
  }
  return COUNTRY_NAME_TO_CODE[normalized] || fallback;
}

export function isTurkeyCountry(country) {
  return normalizeCountryCode(country) === 'TR';
}
